/**
 * Color Picker API - Main interface for the color picker system
 * Integrates with editorConfig.js and provides clean API for UI components
 */

import { ColorPickerCore } from './colorPickerCore.js';
import { favoritesManager } from './favoritesManager.js';
import { hexToRgb, rgbToHex, parseColor, formatColor } from './colorUtils.js';

/**
 * Color Picker API class
 * Main interface for interacting with the color picker system
 */
class ColorPickerAPI {
  constructor() {
    this.core = new ColorPickerCore({
      defaultColors: [],
      format: 'hex',
    });

    this.isInitialized = false;
  }

  /**
   * Initialize the color picker
   * @param {object} options - Configuration options
   * @returns {Promise} Initialization promise
   */
  async init(options = {}) {
    if (this.isInitialized) {
      return this;
    }

    // Dynamically import editorConfig to avoid circular dependencies
    try {
      const { default: editorConfig } = await import(
        '../../~config/editorConfig.js'
      );
      this.core.setDefaultColors(editorConfig.classicColors || []);
      this.core.setClassicColors(editorConfig.classicColors || []);
    } catch (error) {
      console.warn(
        'Impossibile caricare editorConfig, uso colori predefiniti vuoti:',
        error
      );
      this.core.setDefaultColors([]);
    }

    // Apply any custom options
    if (options.format) {
      this.core.setFormat(options.format);
    }

    if (options.defaultColor) {
      this.core.setColor(options.defaultColor);
    }

    this.isInitialized = true;
    return this;
  }

  /**
   * Get the color picker core instance
   * @returns {ColorPickerCore} Core instance
   */
  getCore() {
    return this.core;
  }

  /**
   * Get favorites manager instance
   * @returns {FavoritesManager} Favorites manager
   */
  getFavoritesManager() {
    return favoritesManager;
  }

  /**
   * Create a new color picker instance
   * @param {HTMLElement} container - Container element
   * @param {object} options - Configuration options
   * @returns {Promise<ColorPickerUI>} Color picker UI instance
   */
  async createPicker(container, options = {}) {
    if (!this.isInitialized) {
      await this.init();
    }

    // Dynamic import to avoid circular dependencies
    const { ColorPickerUI } = await import('./colorPickerUI.js');

    const picker = new ColorPickerUI(container, {
      ...options,
      api: this,
    });

    await picker.render();
    return picker;
  }

  /**
   * Quick color selection helper
   * @param {string} color - Initial color
   * @param {Function} onSelect - Selection callback
   * @param {object} options - Configuration options
   * @returns {Promise<ColorPickerUI>} Color picker instance
   */
  async quickPick(color, onSelect, options = {}) {
    // Create temporary container
    const container = document.createElement('div');
    container.className = 'color-picker-quick-modal';
    document.body.appendChild(container);

    const picker = await this.createPicker(container, {
      ...options,
      mode: 'modal',
      initialColor: color,
      onSelect: (selectedColor) => {
        // Clean up
        container.remove();
        if (onSelect) onSelect(selectedColor);
      },
      onCancel: () => {
        container.remove();
      },
    });

    return picker;
  }

  /**
   * Get default colors from editor config
   * @returns {string[]} Array of default colors
   */
  getDefaultColors() {
    return this.core.getDefaultColors();
  }

  /**
   * Get current color
   * @param {string} format - Output format
   * @returns {string} Current color
   */
  getCurrentColor(format) {
    return this.core.getCurrentColor(format);
  }

  /**
   * Set current color
   * @param {string|object} color - Color to set
   * @returns {boolean} Success status
   */
  setColor(color) {
    return this.core.setColor(color);
  }

  /**
   * Generate color palette
   * @param {string} baseColor - Base color
   * @param {string} type - Palette type
   * @returns {string[]} Generated palette
   */
  generatePalette(baseColor, type) {
    return this.core.generatePalette(baseColor, type);
  }

  /**
   * Validate color
   * @param {string} color - Color to validate
   * @returns {boolean} Validation result
   */
  validateColor(color) {
    return this.core.validateColor(color);
  }

  /**
   * Add event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    this.core.on(event, callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  off(event, callback) {
    this.core.off(event, callback);
  }

  /**
   * Get color information
   * @returns {object} Color information
   */
  getColorInfo() {
    return this.core.getColorInfo();
  }

  /**
   * Utility methods for common operations
   */
  utils = {
    /**
     * Convert hex to RGB
     * @param {string} hex - Hex color
     * @returns {object} RGB object
     */
    hexToRgb: (hex) => {
      return hexToRgb(hex);
    },

    /**
     * Convert RGB to hex
     * @param {number} r - Red value
     * @param {number} g - Green value
     * @param {number} b - Blue value
     * @returns {string} Hex color
     */
    rgbToHex: (r, g, b) => {
      return rgbToHex(r, g, b);
    },

    /**
     * Parse color string
     * @param {string} color - Color string
     * @returns {object} Parsed color
     */
    parseColor: (color) => {
      return parseColor(color);
    },

    /**
     * Format color
     * @param {number} r - Red value
     * @param {number} g - Green value
     * @param {number} b - Blue value
     * @param {number} a - Alpha value
     * @param {string} format - Output format
     * @returns {string} Formatted color
     */
    formatColor: (r, g, b, a, format) => {
      return formatColor(r, g, b, a, format);
    },
  };
}

// Create singleton instance
export const colorPickerAPI = new ColorPickerAPI();

// Export for direct usage
export { ColorPickerAPI };
